import { type KeyboardEvent, type ReactNode, useId, useState } from 'react';

export type CompanyActivityTab = 'CONTACTS' | 'TICKETS' | 'HISTORY';

type CompanyActivityPanelProps = {
  contacts: ReactNode;
  tickets: ReactNode;
  history: ReactNode;
  contactsCount?: number;
  ticketsCount?: number;
  activeTab?: CompanyActivityTab;
  onActiveTabChange?: (tab: CompanyActivityTab) => void;
};

const tabs: Array<{ id: CompanyActivityTab; label: string }> = [
  { id: 'CONTACTS', label: 'Контакты' },
  { id: 'TICKETS', label: 'Заявки' },
  { id: 'HISTORY', label: 'История' },
];

export function CompanyActivityPanel({
  contacts,
  tickets,
  history,
  contactsCount,
  ticketsCount,
  activeTab,
  onActiveTabChange,
}: CompanyActivityPanelProps) {
  const [internalTab, setInternalTab] = useState<CompanyActivityTab>('CONTACTS');
  const selectedTab = activeTab ?? internalTab;
  const panelId = useId();
  const content = selectedTab === 'CONTACTS' ? contacts : selectedTab === 'TICKETS' ? tickets : history;
  const count = (tab: CompanyActivityTab) => tab === 'CONTACTS' ? contactsCount : tab === 'TICKETS' ? ticketsCount : undefined;

  const selectTab = (tab: CompanyActivityTab) => {
    setInternalTab(tab);
    onActiveTabChange?.(tab);
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    selectTab(tabs[nextIndex].id);
    event.currentTarget.parentElement
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      .item(nextIndex)
      .focus();
  };

  return (
    <section className="company-activity-panel">
      <div className="company-activity-tabs" role="tablist" aria-label="Разделы карточки компании">
        {tabs.map((tab, index) => (
          <button
            id={`${panelId}-${tab.id.toLowerCase()}`}
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selectedTab === tab.id}
            aria-controls={`${panelId}-content`}
            tabIndex={selectedTab === tab.id ? 0 : -1}
            className={selectedTab === tab.id ? 'active' : ''}
            onClick={() => selectTab(tab.id)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            {tab.label}
            {count(tab.id) !== undefined && <span>{count(tab.id)}</span>}
          </button>
        ))}
      </div>
      <div
        id={`${panelId}-content`}
        className="company-activity-content"
        role="tabpanel"
        aria-labelledby={`${panelId}-${selectedTab.toLowerCase()}`}
      >
        {content}
      </div>
    </section>
  );
}
